import React, { useEffect, useState } from 'react';
import { EuiFlexGroup, EuiFlexItem, EuiLoadingContent, EuiText, EuiPanel } from '@elastic/eui';
import { useSelector } from 'react-redux';
import noOrderSvg from '../../assets/img/soup.svg';
import { useDispatch } from 'react-redux';
import { getLiveOrders, resetLiveOrdersPagination } from '../../api/liveOrders/getLiveOrderNewAPI';
import LiveOrdersTable from './liveOrdersTable';
import LiveOrderDetails from './liveOrderDetails';
import './style.css';
import * as types from '../../reduxStore/types/orders';
import useSingleScreenHeightCalc from '../../hooks/useSingleScreenHeightCalc';

export const LiveOrdersComponent = () => {
    const authUser = useSelector((state) => state.auth.user);
    const currentOrderState = useSelector((state) => state.orders.currentOrderState);
    const currentOrderingMode = useSelector((state) => state.orders.currentOrderingMode);
    const liveOrdersPagination = useSelector((state) => state.orders.liveOrdersPagination);
    const dispatch = useDispatch();
    const { screenHeight } = useSingleScreenHeightCalc();

    const [state, setState] = useState({
        apiDataLoaded: false,
    });
    const [notificationPermission, setNotificationPermission] = useState('default');
    const getLiveOrder = async (page = 1, perPage = 10) => {
        dispatch({
            type: 'set-table-loader',
            payload: { loader: true, error: false },
        });
        await dispatch(getLiveOrders(page, perPage)).then((liveOrders) => {
            if (liveOrders.success) {
                setState({
                    ...state,
                    apiDataLoaded: true,
                });
            }
        });
        dispatch({
            type: 'set-table-loader',
            payload: { loader: false, error: false },
        });
    };

    // Effect for filter changes - only reset pagination
    useEffect(() => {
        // Reset pagination to page 1 when filters change
        if (liveOrdersPagination.currentPage !== 1) {
            dispatch(resetLiveOrdersPagination());
        }
    }, [currentOrderState, currentOrderingMode]);

    // Combined effect for fetching orders - handles pagination, filter and permission changes
    useEffect(() => {
        // Only fetch orders if both device toggle is on AND browser notifications are granted
        if (authUser.accepting_orders && notificationPermission === 'granted') {
            getLiveOrder(liveOrdersPagination.currentPage, liveOrdersPagination.perPage);
        }
    }, [liveOrdersPagination.currentPage, liveOrdersPagination.perPage, currentOrderState, currentOrderingMode, notificationPermission]);

    useEffect(() => {
        return () => {
            dispatch({
                type: types.SET_SELECTED_ORDER_DETAILS,
                payload: {},
            });
            dispatch({
                type: types.SET_SELECTED_ORDER,
                payload: '',
            });
        };
    }, []);

    // Check browser notification permission
    useEffect(() => {
        // Check initial permission
        if ('Notification' in window) {
            setNotificationPermission(Notification.permission);
        }

        // Check permission when window gains focus (user might change in browser settings)
        const handleFocus = () => {
            if ('Notification' in window) {
                setNotificationPermission(Notification.permission);
            }
        };

        window.addEventListener('focus', handleFocus);

        return () => {
            window.removeEventListener('focus', handleFocus);
        };
    }, []);

    const displayInformativeMessage = () => {
        // Check device toggle first
        if (!authUser.accepting_orders) {
            return (
                <div>
                    <div className='no-order-section-child'>
                        <img src={noOrderSvg} className='no-order-image' alt='' />
                        <br />
                        <EuiText>
                            <h3 style={{ textAlign: 'center' }}>Please on Notification toggle to see Orders</h3>
                        </EuiText>
                    </div>
                </div>
            );
        }

        // Then check browser notification permission
        if (notificationPermission !== 'granted') {
            return (
                <div>
                    <div className='no-order-section-child'>
                        <img src={noOrderSvg} className='no-order-image' alt='' />
                        <br />
                        <EuiText>
                            <h3 style={{ textAlign: 'center' }}>
                                Please enable notifications in your browser to see Orders
                            </h3>
                        </EuiText>
                    </div>
                </div>
            );
        }
    };

    const loaderContent = () => {
        let accepting_orders = localStorage.getItem('user')
            ? JSON.parse(localStorage.getItem('user')).accepting_orders
            : 1;

        // Only show loader if both conditions are met
        if (accepting_orders && notificationPermission === 'granted') {
            return !state.apiDataLoaded ? (
                <EuiPanel>
                    <EuiLoadingContent lines={10} />
                </EuiPanel>
            ) : null;
        }
    };

    return (
        <>
            {
                //Message for No order or show order toggle
                displayInformativeMessage()
            }
            <EuiFlexGroup>
                <EuiFlexItem grow={6}>
                    {loaderContent()}
                    {state.apiDataLoaded && authUser.accepting_orders && notificationPermission === 'granted' && (
                        <>
                            <EuiPanel paddingSize='s' style={{ height: screenHeight }} className=' scrollbar-sm'>
                                <LiveOrdersTable />
                            </EuiPanel>
                        </>
                    )}
                </EuiFlexItem>
                <EuiFlexItem grow={4}>
                    {loaderContent()}
                    {state.apiDataLoaded && authUser.accepting_orders && notificationPermission === 'granted' && (
                        <div>
                            <EuiPanel paddingSize='s' style={{ height: screenHeight }} className=' scrollbar-sm'>
                                <LiveOrderDetails isLiveOrder={true} />
                            </EuiPanel>
                        </div>
                    )}
                </EuiFlexItem>
            </EuiFlexGroup>
        </>
    );
};
